import React from "react";
import InputLabel from "@mui/material/InputLabel";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePickerProps } from "@mui/x-date-pickers/DesktopDatePicker";
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { DEFAULT_DATE_FORMAT } from "@/utils/constants";
import DatePickerIcon from "./datePickerIcon";

import "@/utils/dayjs-config";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("UTC");

interface IDateInput extends DesktopDatePickerProps {
  id?: string;
  name?: string;
  label?: string;
  value?: Dayjs | null;
  errorText?: any | string | null;
  options?: React.ReactNode;
  clearable?: boolean;
  labelSx?: any;
}

function DateInput({
  id,
  name,
  label,
  errorText,
  options,
  clearable = false,
  labelSx,
  ...rest
}: IDateInput) {
  const [open, setOpen] = React.useState(false);
  const generatedId = React.useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const inputId = id || `date-picker-${generatedId}`;

  return (
    <div className="date-input-root" style={{ width: "100%", minWidth: 0 }}>
      {label && (
        <InputLabel
          sx={{
            color: "#1F4970",
            fontSize: "14px",
            fontWeight: "600",
            padding: "0px 0px 8px 14px",
            width: "100%",
            textTransform: "capitalize",
            "& .MuiSelect-select.Mui-disabled": {
              WebkitTextFillColor: "#000000 !important",
              color: "#000000 !important",
            },
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: "#000000 !important",
              color: "#000000 !important",
            },
            ...labelSx,
          }}
          {...(typeof label === "string" && { htmlFor: inputId })}
        >
          {label}
        </InputLabel>
      )}

      {options && options}

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          className="date_picker_error"
          timezone="UTC"
          format={rest?.format || DEFAULT_DATE_FORMAT}
          {...rest}
          sx={[
            {
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              "& .MuiOutlinedInput-root": {
                fontSize: "0.875rem",
              },
              "& .MuiPickersInputBase-root": {
                width: "100%",
                minWidth: 0,
              },
            },
            errorText
              ? {
                  "& .MuiOutlinedInput-root": {
                    borderColor: "#FF0000",
                  },
                }
              : {
                  "& .MuiOutlinedInput-root": {
                    borderColor: "",
                  },
                },
            errorText
              ? {
                  "& .MuiOutlinedInput-root": {
                    color: "#FA896B",
                  },
                }
              : {
                  "& .MuiOutlinedInput-root": {
                    color: " #1F4970",
                  },
                },
            errorText
              ? {
                  "& .MuiOutlinedInput-root": {
                    border: "1px solid #F00",
                  },
                }
              : {
                  "& .MuiOutlinedInput-root": {
                    border: "",
                  },
                },
          ]}
          open={rest?.disabled ? false : open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          slots={{
            openPickerIcon: (props) => (
              <DatePickerIcon
                {...props}
                color={rest?.disabled ? "#BDBDBD" : "#1F4970"}
              />
            ),
          }}
          slotProps={{
            textField: {
              error: !!errorText,
              helperText: errorText,
              style: {
                color: errorText ? "#F00" : "#000",
              },
              onClick: () => setOpen(true),
              id: inputId,
              name: name || inputId,
            },
            actionBar: {
              actions: clearable ? ["clear"] : [],
            },
          }}
        />
      </LocalizationProvider>
    </div>
  );
}

export default DateInput;
